<script setup>
import {onMounted, ref, watch} from "vue";
import Button from "../../components/forms/Button.vue";
import {useRouter} from "vue-router";
import {getApiBaseUrl, showSuccessMessage} from "../../helper";
import {createConfirmDialog} from "vuejs-confirm-dialog";
import IntermediaryService from "@/services/IntermediaryService";
import CreateUpdateIntermediaryPersonModal from "@/components/modals/CreateUpdateIntermediaryPersonModal.vue";
import {getLoggedUser} from "@/services/AuthService";
import IntermediariesPeopleListComponent from "@/components/intermediaries-people/IntermediariesPeopleListComponent.vue";
import CreateUpdateIntermediaryModal from "@/components/modals/CreateUpdateIntermediaryModal.vue";
import CustomDropzone from "@/components/forms/CustomDropzone.vue";
import ModalDialog from "@/components/modals/ModalDialog.vue";
import {useI18n} from 'vue-i18n';

const {t} = useI18n()
const router = useRouter();
const intermediaryId = router.currentRoute.value.params.id;
const intermediary = ref({});
const isLoading = ref(true);
const isEditable = ref(false);

/** Iniciály z názvu zprostředkovatele — reaktivně sledovány přes watch */
const intermediaryInitials = ref('');

/** Aktivní záložka v detailní kartě */
const activeTab = ref('basic');

onMounted(() => {
  fetchIntermediary();
  getLoggedUser().then(response => {
    const loggedUser = response;
    // IMA-510: INTERMEDIARY_MANAGER spravuje admin. celého vlastního SFA (backend to vynucuje
    // přes assertOwnIntermediary - jiné SFA se sem vůbec nedostane).
    if (['ADMIN', 'SUPERADMIN', 'BACK_OFFICE', 'INTERMEDIARY_MANAGER'].includes(loggedUser.role)) {
      isEditable.value = true;
    }
  });
});

/**
 * Reaguje na změnu celého objektu intermediary (watch místo computed,
 * protože celý ref je nahrazen novou hodnotou z API).
 */
watch(intermediary, (val) => {
  if (!val || !val.intermediary_name) {
    intermediaryInitials.value = '?';
    return;
  }
  intermediaryInitials.value = val.intermediary_name.charAt(0).toUpperCase();
}, {deep: true});

function fetchIntermediary() {
  isLoading.value = true;
  IntermediaryService.getIntermediary(intermediaryId).then((response) => {
    intermediary.value = response;
    isLoading.value = false;
  }, () => {
    // IMA-510: cizí/nepřístupné SFA (403 od backendu) - hláška už zobrazil IntermediaryService,
    // tady jen neuvízneme navždy na "načítání" a vrátíme uživatele zpět.
    isLoading.value = false;
    router.back();
  });
}

function updateIntermediary() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(CreateUpdateIntermediaryModal, intermediary.value ? {
    intermediary: intermediary.value,
  } : {});
  reveal();
  onConfirm((data) => {
    IntermediaryService.saveIntermediary(data, intermediaryId).then(response => {
      showSuccessMessage(response.message);
      intermediary.value = response.intermediary;
    });
  });
  onCancel(() => {});
}

function createUpdateIntermediaryPerson(intermediaryPerson = null) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(CreateUpdateIntermediaryPersonModal, intermediaryPerson ? {
    intermediaryPerson: intermediaryPerson,
    intermediary: intermediary.value
  } : {intermediary: intermediary.value});
  reveal();
  onConfirm((person) => {
    emitter.emit('intermediaryPersonSaved', person);
  });
  onCancel(() => {});
}

function deleteIntermediaryLogo() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ModalDialog, {
    title: t('intermediariesDetail.logo_removal'),
    message: t('intermediariesDetail.logo_removal_confirm')
  });
  onConfirm(() => {
    IntermediaryService.deleteIntermediaryLogo(intermediary.value.id).then(() => {
      intermediary.value.logo_base64 = null;
      showSuccessMessage(t('intermediariesDetail.logo_deleted'));
    });
  });
  onCancel(() => {});
  reveal();
}

function onUploadSuccess(file, response) {
  intermediary.value = response.intermediary;
}

function onUploadError() {
  console.log('error');
}

</script>

<template>
  <div class="page-section">
    <div class="page-section__heading">
      <div class="row">
        <div class="col-4">
          <a @click="router.back()" class="page-section__heading__back"><i class="mdi mdi-arrow-left"></i> Zpět</a>
        </div>
        <div class="col-4 text-center">
          <div class="page-section__title">{{ t('intermediariesDetail.detail_broker') }}</div>
        </div>
        <div class="col-4"></div>
      </div>
    </div>

    <div class="page-section__content">

      <!-- Vizitka zprostředkovatele — overflow:visible kvůli dropdown menu -->
      <div class="card mb-4" style="overflow: visible;">
        <div class="card-body py-3">
          <div class="d-flex align-items-start justify-content-between flex-wrap gap-3">

            <!-- Levá část: avatar + název + kód -->
            <div class="d-flex align-items-start gap-3">
              <!-- Kruhový avatar s prvním písmenem názvu zprostředkovatele -->
              <div class="intermediary-avatar">{{ intermediaryInitials }}</div>

              <div>
                <!-- Název + kód na jednom řádku — kód jako prostý text (stejně jako u klienta) -->
                <div class="d-flex align-items-center flex-wrap gap-2 mb-1">
                  <h5 class="mb-0 fw-semibold">{{ intermediary.intermediary_name ?? '---' }}</h5>
                  <span class="text-muted small">
                    {{ t('intermediariesDetail.code') }} <strong class="text-body">{{ intermediary.code ?? '---' }}</strong>
                  </span>
                </div>
               </div>
            </div>

            <!-- Pravá část: kontaktní info -->
            <div class="text-end small text-muted">
              <div v-if="intermediary.email" class="mb-1">{{ intermediary.email }}</div>
              <div v-if="intermediary.email_back_office" class="mb-1">{{ intermediary.email_back_office }}</div>
              <div v-if="intermediary.phone">{{ intermediary.phone }}</div>
            </div>

          </div>
        </div>
      </div>

      <!-- Karta se záložkami (základní info + kontakt) a karta loga — vedle sebe -->
      <div class="row mb-4">
        <div class="col-12 col-md-6">
          <div class="card h-100">
            <div class="card-header">
              <div class="row">
                <div class="col">
                  <div class="card-header__title">{{ t('intermediariesDetail.intermediary_data') }}</div>
                </div>
                <div class="col text-end">
                  <div class="card-header__action">
                    <Button v-if="isEditable" :label="t('intermediariesDetail.edit')" @click="updateIntermediary()"/>
                  </div>
                </div>
              </div>
            </div>
            <div class="card-body">

              <!-- Záložky — navigace, stejný pattern jako ClientsDetail (taby v card-body) -->
              <ul class="nav nav-tabs mb-3">
                <li class="nav-item">
                  <a class="nav-link ps-2 pe-3" :class="{ active: activeTab === 'basic' }" href="#" @click.prevent="activeTab = 'basic'">
                    {{ t('intermediariesDetail.tab_basic') }}
                  </a>
                </li>
                <li class="nav-item">
                  <a class="nav-link px-3" :class="{ active: activeTab === 'contact' }" href="#" @click.prevent="activeTab = 'contact'">
                    {{ t('intermediariesDetail.tab_contact') }}
                  </a>
                </li>
              </ul>

              <!-- Záložka: základní informace -->
              <div v-show="activeTab === 'basic'" class="info-table">
                <div class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediariesDetail.name') }}</span>
                  <span class="info-table__value col-12">{{ intermediary.intermediary_name ?? '---' }}</span>
                </div>
                <div class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediariesDetail.abbr') }}</span>
                  <span class="info-table__value col-12">{{ intermediary.abbr ?? '---' }}</span>
                </div>
                <div class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediariesDetail.code') }}</span>
                  <span class="info-table__value col-12">{{ intermediary.code ?? '---' }}</span>
                </div>
                <div class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediariesDetail.ico') }}</span>
                  <span class="info-table__value col-12">{{ intermediary.business_number ?? '---' }}</span>
                </div>
                <div class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediariesDetail.dic') }}</span>
                  <span class="info-table__value col-12">{{ intermediary.tax_number ?? '---' }}</span>
                </div>
                <div class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediariesDetail.bank_account_number') }}</span>
                  <span class="info-table__value col-12">{{ intermediary.bank_account_number ?? '---' }}</span>
                </div>
                <div class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediariesDetail.iban') }}</span>
                  <span class="info-table__value col-12">{{ intermediary.iban ?? '---' }}</span>
                </div>
                <div class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediariesDetail.company_address') }}</span>
                  <span class="info-table__value col-12">{{ intermediary.address?.full_address ?? '---' }}</span>
                </div>
              </div>

              <!-- Záložka: kontakt -->
              <div v-show="activeTab === 'contact'" class="info-table">
                <div class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediariesDetail.email') }}</span>
                  <span class="info-table__value col-12">{{ intermediary.email ?? '---' }}</span>
                </div>
                <div class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediariesDetail.email_back_office') }}</span>
                  <span class="info-table__value col-12">{{ intermediary.email_back_office ?? '---' }}</span>
                </div>
                <div class="info-table__row row">
                  <span class="info-table__label col-12">{{ t('intermediariesDetail.phone_number') }}</span>
                  <span class="info-table__value col-12">{{ intermediary.phone ?? '---' }}</span>
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- Karta loga — samostatná, jako původně -->
        <div class="col-12 col-md-6">
          <div class="card h-100">
            <div class="card-header">
              <div class="row">
                <div class="col">
                  <div class="card-header__title">{{ t('intermediariesDetail.logo') }}</div>
                </div>
              </div>
            </div>
            <div class="card-body">
              <div v-if="intermediary.logo_base64" class="mb-4">
                <div class="fw-bold mb-2">{{ t('intermediariesDetail.preview_of_logo') }}</div>
                <div class="row d-flex align-items-center">
                  <div class="col-12 col-md-6">
                    <img class="intermediary-logo--preview" :src="intermediary.logo_base64" alt="Logo"/>
                  </div>
                  <div v-if="isEditable" class="col-12 col-md-6 d-flex justify-content-end">
                    <Button :label="t('intermediariesDetail.remove_logo')" @click="deleteIntermediaryLogo()"/>
                  </div>
                </div>
              </div>
              <div v-if="intermediary.id">
                <label class="form-label">{{ t('intermediariesDetail.logo') }}</label>
                <CustomDropzone :dropzone-options-input="{
                  url: getApiBaseUrl() + '/api/intermediaries/' + intermediary.id + '/logo/upload',
                  acceptedFiles: '.jpg, .jpeg, .png',
                  uploadMultiple: false,
                  paramName: 'logo',
                  init() {}
                }" :upload-success="onUploadSuccess" :upload-error="onUploadError"/>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Karta zástupců zprostředkovatele -->
      <div class="card">
        <div class="card-header">
          <div class="row">
            <div class="col">
              <div class="card-header__title">{{ t('intermediariesDetail.intermediaries_people') }}</div>
            </div>
            <div class="col text-end">
              <div class="card-header__action">
                <Button v-if="isEditable" :label="t('intermediariesDetail.intermediaries_people_add')" @click="createUpdateIntermediaryPerson()"/>
              </div>
            </div>
          </div>
        </div>
        <div class="card-body">
          <IntermediariesPeopleListComponent v-if="!isLoading" :entityId="intermediary.id"/>
        </div>
      </div>

    </div>
  </div>
</template>

<style scoped>
/* Avatar zprostředkovatele — kruh s iniciálou */
.intermediary-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: var(--primaryColor);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 600;
  flex-shrink: 0;
}

/* Nav-tabs: pouze spodní podtržení aktivní záložky (bez výplně) */
:deep(.nav-tabs) {
  border-bottom: none;
}
:deep(.nav-tabs .nav-link) {
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--bs-body-color);
  padding-bottom: 0.75rem;
}
:deep(.nav-tabs .nav-link.active) {
  border-bottom-color: var(--primaryColor);
  color: var(--primaryColor);
  background: transparent;
  font-weight: 600;
}
:deep(.nav-tabs .nav-link:hover:not(.active)) {
  border-bottom-color: #dee2e6;
}

.intermediary-logo--preview {
  max-width: 100%;
  height: auto;
  margin-top: 10px;
}
</style>